import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { TrackJS } from 'trackjs';
import { useLocation } from 'react-router';
import { useApp } from 'providers/AppProvider';
import { useAuth } from 'providers/AuthProvider';

export const usePendo = () => {
  const { vendorUUID, email } = useApp();

  useEffect(() => {
    // Initialize Pendo
    window.pendo.initialize();
  }, []);

  useEffect(() => {
    if (!vendorUUID) {
      return;
    }

    // Identify the user to Pendo once they have logged in
    window.pendo.identify({
      visitor: {
        id: email,
        vendorUUID,
      },
    });
  }, [vendorUUID, email]);
};

// Returns the userId that is temporarily stored during registration
const useUnregisteredUserId = () => {
  const { state = {}, pathname } = useLocation();

  if (pathname !== '/register') {
    return null;
  }

  return state.userId;
};

export const useGTM = process.env.REACT_APP_GTM_ID
  ? () => {
      useEffect(() => {
        TagManager.initialize({
          gtmId: process.env.REACT_APP_GTM_ID,
        });
      }, []);

      const { user, account } = useAuth();

      // A user is created when subscribing and is therefore
      // identifiable even before completing registration
      const unregisteredUserId = useUnregisteredUserId();

      const userUuid = user ? user.id : unregisteredUserId;
      const vendorUuid = account && account.vendor ? account.vendor.id : null;

      useEffect(() => {
        if (!userUuid) {
          return;
        }

        TagManager.dataLayer({
          dataLayer: {
            // Previously the GA tracking calls used vendorUuid as the userId.
            // This preserves the same behaviour for now but we can abandon backwards
            // compatbility later on.
            userId: vendorUuid,

            vendorUuid,
            userUuid,
          },
        });
      }, [userUuid, vendorUuid]);
    }
  : // No-op hook
    () => {};

export const useTrackJS = () => {
  const { vendorUUID } = useApp();

  useEffect(() => {
    TrackJS.addMetadata('userId', vendorUUID);
  }, [vendorUUID]);
};

export default () => {
  usePendo();
  useGTM();
  useTrackJS();
};
